import type { FormulaDefinition, FormulaImplementation } from "./formula-registry";

type Definition = Omit<FormulaDefinition, "schemaVersion" | "version" | "warmupBars" | "nullPolicy" | "knownAtPolicy" | "goldenFixtureIds">;

function formula(definition: Definition): FormulaDefinition {
  return {
    schemaVersion: "tradvera.formula-definition.v1",
    version: "1.0.0",
    warmupBars: 0,
    nullPolicy: "abstain",
    knownAtPolicy: "latest_known_at_evaluation",
    goldenFixtureIds: [`golden:${definition.id}:v1`],
    ...definition,
  };
}

function inputs(...definitions: Array<[id: string, unit: string]>) {
  return definitions.map(([id, unit]) => ({ id, unit, required: true as const }));
}

function growthGap(id: string, explainId: string, balanceSheetLabel: string) {
  return formula({
    id,
    family: "earnings_quality",
    implementation: "growth_gap" as FormulaImplementation,
    notation: `${balanceSheetLabel} growth − Revenue growth`,
    inputs: inputs(["balance_sheet_growth", "decimal"], ["revenue_growth", "decimal"]),
    outputUnit: "decimal",
    explainId,
  });
}

export const MARKET_FORMULA_CATALOG: FormulaDefinition[] = [
  formula({
    id: "market.advance_decline_pct",
    family: "market_breadth",
    implementation: "advance_decline_pct",
    notation: "(Advancers − Decliners) / Eligible universe",
    inputs: inputs(["advancers", "count"], ["decliners", "count"], ["eligible_universe", "count"]),
    outputUnit: "decimal",
    explainId: "market.breadth.advance_decline",
  }),
  formula({
    id: "market.pct_above_ma",
    family: "market_breadth",
    implementation: "pct_above_ma",
    notation: "Count(Close > MA_n) / Eligible universe",
    inputs: inputs(["count_above_ma", "count"], ["eligible_universe", "count"]),
    outputUnit: "decimal",
    explainId: "market.breadth.pct_above_ma",
  }),
  formula({
    id: "market.new_high_low_pct",
    family: "market_breadth",
    implementation: "new_high_low_pct",
    notation: "(New highs − New lows) / Eligible universe",
    inputs: inputs(["new_highs", "count"], ["new_lows", "count"], ["eligible_universe", "count"]),
    outputUnit: "decimal",
    explainId: "market.breadth.new_high_low",
  }),
  formula({
    id: "market.price_relative",
    family: "relative_strength",
    implementation: "price_relative",
    notation: "Security price / Benchmark price",
    inputs: inputs(["security_price", "currency"], ["benchmark_price", "currency"]),
    outputUnit: "ratio",
    explainId: "market.relative_strength.price_relative",
  }),
  formula({
    id: "market.relative_return_spread",
    family: "relative_strength",
    implementation: "relative_return_spread",
    notation: "Security return − Benchmark return",
    inputs: inputs(["security_return", "decimal"], ["benchmark_return", "decimal"]),
    outputUnit: "decimal",
    explainId: "market.relative_strength.return_spread",
  }),
  formula({
    id: "expectations.surprise_pct",
    family: "expectations",
    implementation: "surprise_pct",
    notation: "(Actual − Consensus) / abs(Consensus)",
    inputs: inputs(["actual", "metric_unit"], ["consensus", "metric_unit"]),
    outputUnit: "decimal",
    explainId: "expectations.surprise",
  }),
  formula({
    id: "expectations.revision_pct",
    family: "expectations",
    implementation: "revision_pct",
    notation: "(Consensus now − Consensus then) / abs(Consensus then)",
    inputs: inputs(["consensus_now", "metric_unit"], ["consensus_then", "metric_unit"]),
    outputUnit: "decimal",
    explainId: "expectations.revision",
  }),
  formula({
    id: "quality.cfo_to_net_income",
    family: "earnings_quality",
    implementation: "cfo_to_net_income",
    notation: "Cash flow from operations / Net income",
    inputs: inputs(["cfo", "currency"], ["net_income", "currency"]),
    outputUnit: "ratio",
    explainId: "quality.cfo_to_net_income",
  }),
  formula({
    id: "quality.accrual_ratio",
    family: "earnings_quality",
    implementation: "accrual_ratio",
    notation: "(Net income − CFO) / abs(Average total assets)",
    inputs: inputs(["net_income", "currency"], ["cfo", "currency"], ["average_total_assets", "currency"]),
    outputUnit: "decimal",
    explainId: "quality.accrual_ratio",
  }),
  growthGap("quality.ar_sales_gap", "quality.ar_sales_gap", "Accounts receivable"),
  growthGap("quality.inventory_sales_gap", "quality.inventory_sales_gap", "Inventory"),
];
